import { Texture } from 'pixi.js';
import { TRANSPARENT, slotIndex, type PaletteInstance, type SpriteDef } from './types';

const cache = new Map<string, Texture[]>();

/** Rasterize one frame onto a fresh canvas (also used by the gallery). */
export function frameToCanvas(
  def: SpriteDef,
  frameIndex: number,
  palette: PaletteInstance,
  scale = 1,
): HTMLCanvasElement {
  const canvas = document.createElement('canvas');
  canvas.width = def.w * scale;
  canvas.height = def.h * scale;
  const ctx = canvas.getContext('2d')!;
  const image = ctx.createImageData(def.w, def.h);
  const rows = def.frames[frameIndex] ?? [];
  rows.forEach((row, y) => {
    for (let x = 0; x < row.length; x += 1) {
      const char = row[x]!;
      if (char === TRANSPARENT) continue;
      const color = palette.colors[slotIndex(char)] ?? 0xff00ff;
      const offset = (y * def.w + x) * 4;
      image.data[offset] = (color >> 16) & 0xff;
      image.data[offset + 1] = (color >> 8) & 0xff;
      image.data[offset + 2] = color & 0xff;
      image.data[offset + 3] = 255;
    }
  });
  if (scale === 1) {
    ctx.putImageData(image, 0, 0);
    return canvas;
  }
  const small = document.createElement('canvas');
  small.width = def.w;
  small.height = def.h;
  small.getContext('2d')!.putImageData(image, 0, 0);
  ctx.imageSmoothingEnabled = false;
  ctx.drawImage(small, 0, 0, canvas.width, canvas.height);
  return canvas;
}

/** Bake every frame of a sprite for a palette. Cached by name:palette. */
export function bakeFrames(def: SpriteDef, palette: PaletteInstance): Texture[] {
  const key = `${def.name}:${palette.id}`;
  const hit = cache.get(key);
  if (hit) return hit;
  const textures = def.frames.map((_, index) =>
    Texture.from(frameToCanvas(def, index, palette)),
  );
  cache.set(key, textures);
  return textures;
}

export function clearArtCache(): void {
  for (const textures of cache.values()) {
    for (const texture of textures) texture.destroy(true);
  }
  cache.clear();
}
